import { dateLocale } from "@/i18n"

const locale = () => dateLocale().code

/** Money with sensible precision for tiny per-run costs ("$0.0042", "€12.30"). */
export function formatCost(value: number | null | undefined, currency = "USD"): string {
  if (value == null || Number.isNaN(value)) return "—"
  const abs = Math.abs(value)
  const digits = abs === 0 ? 2 : abs < 0.01 ? 4 : abs < 1 ? 3 : 2
  try {
    return new Intl.NumberFormat(locale(), {
      style: "currency",
      currency,
      minimumFractionDigits: Math.min(digits, 2),
      maximumFractionDigits: digits,
    }).format(value)
  } catch {
    return `${value.toFixed(digits)} ${currency}`
  }
}

/** Compact token counts: 950 → "950", 12_400 → "12.4K", 1_200_000 → "1.2M". */
export function formatTokens(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—"
  return new Intl.NumberFormat(locale(), {
    notation: value >= 10_000 ? "compact" : "standard",
    maximumFractionDigits: 1,
  }).format(value)
}

/** Durations: 420 ms → "420 ms", 3_400 → "3.4 s", 125_000 → "2m 5s", 3_900_000 → "1h 5m". */
export function formatDuration(ms: number | null | undefined): string {
  if (ms == null || Number.isNaN(ms) || ms < 0) return "—"
  if (ms < 1000) return `${Math.round(ms)} ms`
  const s = ms / 1000
  if (s < 60) return `${s < 10 ? s.toFixed(1) : Math.round(s)} s`
  const totalSec = Math.round(s)
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const sec = totalSec % 60
  if (h > 0) return `${h}h ${m}m`
  return `${m}m ${sec}s`
}

/** Pretty-print a tool argument/result: JSON when possible, raw text otherwise. */
export function prettyJson(value: unknown): string {
  if (value == null) return ""
  if (typeof value === "string") {
    const trimmed = value.trim()
    if (
      (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
      (trimmed.startsWith("[") && trimmed.endsWith("]"))
    ) {
      try {
        return JSON.stringify(JSON.parse(trimmed), null, 2)
      } catch {
        return value
      }
    }
    return value
  }
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

/** Rough token estimate (≈ 4 characters per token for English-like text). */
export function estimateTokens(text: string): number {
  if (!text) return 0
  return Math.max(1, Math.round(text.length / 4))
}
